/* API client + SSE subscription. All calls are same-origin (/api proxied
   to the api server in dev, same container in deployment). */
import type { CompanyProfile, Entity, InterventionProfile, Strategy, JobStep } from '@flourish/schema';
import type { PortfolioProposal, Suggestion } from '@flourish/suggester';

export interface EntityView extends Entity {
  provenance: Record<string, { source: string; status: string; updatedBy: string; updatedAt: string }>;
}

export interface ScenarioView {
  id: string; code: string; name: string; full: string; cat: string;
  limit: string; peak: string; tagline: string; mechanism: string;
  failure: string; outcome: string; rationale: string;
  prob: number; focus: boolean; hidden: boolean; defaultProb: number;
}

export interface JobView {
  id: string; type: string; state: string; steps: JobStep[];
  cancellable: boolean; error?: string; resultRef?: string;
}

export interface InferMeta {
  inferred: boolean;
  confidence: number;
  model: string;
  inferredAt: string;
  citations: { name: string; quote: string; evidenceId: string }[];
  sources: { id: string; ref: string; title: string }[];
}

/** Strategies as served: schema Strategy + optional inference metadata. */
export type StrategyView = Strategy & { meta?: InferMeta };

export interface EvidenceView {
  id: string; kind: string; ref: string; title: string; chars: number; retrievedAt: string;
}

export interface SuggestionView {
  field: string; value: unknown; confidence: number;
  evidence: { evidenceId: string; quote: string }[]; createdAt: string;
}

export interface UserView {
  id: string; email: string; name: string; role: string;
  workspaceId?: string; disabled?: boolean; createdAt?: string;
}

export interface ApiKeyView {
  id: string; name: string; prefix: string; role: string;
  createdBy: string; createdAt: string; lastUsedAt?: string; revoked: boolean;
}

export interface SignalView {
  id: string; label: string; headline: string; source: string;
  impacts: { scenarioId: string; delta: number }[];
  rationale: string; status: 'proposed' | 'applied' | 'dismissed';
  createdAt: string; decidedAt?: string; decidedBy?: string;
}

export interface Capabilities {
  entityTypes: Record<string, { status: string; label: string; eta?: string; blurb: string }>;
  features: Record<string, { status: string; note: string }>;
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error((detail as { message?: string; error?: string }).message ?? (detail as { error?: string }).error ?? `${res.status}`);
    (err as Error & { status?: number; detail?: unknown }).status = res.status;
    (err as Error & { detail?: unknown }).detail = detail;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) => req<{ user: UserView }>('POST', '/api/auth/login', { email, password }),
  logout: () => req('POST', '/api/auth/logout'),
  logoutAll: () => req('POST', '/api/auth/logout-all'),
  me: () => req<{ user: UserView }>('GET', '/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    req('POST', '/api/auth/change-password', { currentPassword, newPassword }),
  capabilities: () => req<Capabilities>('GET', '/api/capabilities'),

  entities: () => req<{ entities: EntityView[] }>('GET', '/api/entities'),
  createEntity: (body: { name: string; type?: string; description?: string; profile?: Partial<CompanyProfile> }) =>
    req<{ entity: EntityView }>('POST', '/api/entities', body),
  updateEntity: (id: string, body: { name?: string; description?: string; accent?: string }) =>
    req<{ entity: EntityView }>('PATCH', `/api/entities/${id}`, body),
  deleteEntity: (id: string) => req('DELETE', `/api/entities/${id}`),
  updateProfile: (id: string, fields: Partial<CompanyProfile>) =>
    req<{ profile: CompanyProfile }>('PATCH', `/api/entities/${id}/profile`, fields),

  scenarios: () => req<{ scenarios: ScenarioView[] }>('GET', '/api/scenarios'),
  patchScenario: (id: string, body: { prob?: number; focus?: boolean; hidden?: boolean }) =>
    req<{ scenarios: ScenarioView[] }>('PATCH', `/api/scenarios/${id}`, body),

  strategies: (entityId: string) => req<{ strategies: StrategyView[] }>('GET', `/api/entities/${entityId}/strategies`),
  createStrategy: (entityId: string, body: { name: string; desc?: string; tplKey?: string; interventions: InterventionProfile[] }) =>
    req<{ strategy: Strategy }>('POST', `/api/entities/${entityId}/strategies`, body),
  updateStrategy: (id: string, body: { name?: string; desc?: string; interventions?: InterventionProfile[] }) =>
    req<{ strategy: Strategy }>('PATCH', `/api/strategies/${id}`, body),
  deleteStrategy: (id: string) => req('DELETE', `/api/strategies/${id}`),

  classify: (text: string, name?: string) =>
    req<{ profile: InterventionProfile; suggestion: Suggestion }>('POST', '/api/interventions/classify', { text, name }),
  suggestPortfolio: (text: string) =>
    req<{ proposals: PortfolioProposal[] }>('POST', '/api/strategies/suggest-portfolio', { text }),
  customIvs: (entityId: string) =>
    req<{ interventions: InterventionProfile[] }>('GET', `/api/entities/${entityId}/custom-interventions`),
  deleteCustomIv: (entityId: string, name: string) =>
    req('DELETE', `/api/entities/${entityId}/custom-interventions/${encodeURIComponent(name)}`),

  simulate: (body: { entityId: string; strategyIds?: string[]; interventions?: InterventionProfile[]; scenarioIds?: string[]; includeTraces?: boolean }) =>
    req<{ engineVersion: string; runs: unknown[] }>('POST', '/api/simulate', body),
  optimize: (entityId: string, keepIids: string[] = []) =>
    req<{ jobId: string }>('POST', '/api/optimize/no-regret', { entityId, keepIids }),
  job: (id: string) => req<{ job: JobView }>('GET', `/api/jobs/${id}`),
  run: (id: string) => req<{ payload: unknown }>('GET', `/api/runs/${id}`),

  /* --- intelligence (M2) --- */
  narrate: (entityId: string, strategyId: string, scenarioId: string) =>
    req<{ prose: string; source: string; cached: boolean }>('POST', '/api/narrate', { entityId, strategyId, scenarioId }),
  enrich: (entityId: string, body: { urls?: string[]; text?: string }) =>
    req<{ jobId: string }>('POST', `/api/entities/${entityId}/enrich`, body),
  evidence: (entityId: string) =>
    req<{ evidence: EvidenceView[] }>('GET', `/api/entities/${entityId}/evidence`),
  deleteEvidence: (id: string) => req('DELETE', `/api/evidence/${id}`),
  suggestions: (entityId: string) =>
    req<{ suggestions: SuggestionView[] }>('GET', `/api/entities/${entityId}/suggestions`),
  reviewSuggestion: (entityId: string, field: string, action: 'accept' | 'reject') =>
    req('POST', `/api/entities/${entityId}/suggestions/${field}`, { action }),
  inferMostLikely: (entityId: string) =>
    req<{ jobId: string }>('POST', `/api/infer/most-likely/${entityId}`),

  /* --- signals (M3): propose -> human apply --- */
  signals: () => req<{ signals: SignalView[] }>('GET', '/api/signals'),
  createSignal: (body: { headline: string; source?: string; label?: string; impacts: { scenarioId: string; delta: number }[]; rationale?: string }) =>
    req<{ created: string[] }>('POST', '/api/signals', body),
  analyzeSignal: (headline: string, source?: string) =>
    req<{ signal: SignalView }>('POST', '/api/signals/analyze', { headline, source }),
  applySignal: (id: string) =>
    req<{ applied: { scenarioId: string; from: number; to: number }[]; scenarios: ScenarioView[] }>('POST', `/api/signals/${id}/apply`),
  dismissSignal: (id: string) => req('POST', `/api/signals/${id}/dismiss`),
  sso: () => req<{ enabled: boolean; loginUrl: string | null }>('GET', '/api/auth/sso'),

  /* --- workspace management (T1) --- */
  users: () => req<{ users: UserView[] }>('GET', '/api/users'),
  createUser: (body: { email: string; name?: string; role?: string }) =>
    req<{ user: UserView; oneTimePassword: string }>('POST', '/api/users', body),
  updateUser: (id: string, body: { role?: string; name?: string }) =>
    req<{ user: UserView }>('PATCH', `/api/users/${id}`, body),
  disableUser: (id: string) => req<{ user: UserView }>('POST', `/api/users/${id}/disable`),
  enableUser: (id: string) => req<{ user: UserView }>('POST', `/api/users/${id}/enable`),
  resetUserPassword: (id: string) => req<{ oneTimePassword: string }>('POST', `/api/users/${id}/reset-password`),
  keys: () => req<{ keys: ApiKeyView[] }>('GET', '/api/keys'),
  createKey: (body: { name: string; role?: string }) =>
    req<{ id: string; key: string; prefix: string; role: string }>('POST', '/api/keys', body),
  revokeKey: (id: string) => req('DELETE', `/api/keys/${id}`),
};

/** Subscribe to server events (job progress, domain changes). */
export function subscribe(handlers: {
  onJob?: (job: JobView) => void;
  onChanged?: (kind: string, data: unknown) => void;
}): () => void {
  const es = new EventSource('/api/events');
  es.addEventListener('job', (e) => handlers.onJob?.(JSON.parse((e as MessageEvent).data)));
  for (const kind of ['strategies.changed', 'scenarios.changed', 'entity.changed', 'signals.changed']) {
    es.addEventListener(kind, (e) => handlers.onChanged?.(kind, JSON.parse((e as MessageEvent).data)));
  }
  return () => es.close();
}
